import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Select,
} from 'antd';
import PropTypes from 'prop-types';
import { fetchMedications } from '../../../medications/api/medicationsApi';
import {
  createTherapy,
  fetchAcuteTherapySuggestion,
  fetchUserTherapies,
} from '../../../therapies/api/therapiesApi';
import fixedBottomDatePickerProps from '../../../../shared/utils/datePickerPlacement';

const initialForm = {
  centralDate: '',
  durationMonths: '',
  medications: [],
  isAcute: false,
  description: '',
};

const durationOptions = ['1', '2', '3', '4', '5', '6'];

const getInitialForm = ({
  defaultIsAcute = false,
  defaultDurationMonths = null,
}) => {
  const normalizedIsAcute = defaultIsAcute === true;
  const fallbackDuration = normalizedIsAcute ? '1' : '';

  return {
    ...initialForm,
    isAcute: normalizedIsAcute,
    durationMonths: String(defaultDurationMonths || fallbackDuration),
  };
};

const AddTherapyModal = ({
  isOpen,
  onClose,
  user,
  onCreate,
  defaultIsAcute = false,
  lockIsAcute = false,
  defaultDurationMonths = null,
  lockDurationMonths = false,
}) => {
  const initialModalForm = useMemo(
    () =>
      getInitialForm({
        defaultIsAcute,
        defaultDurationMonths,
      }),
    [defaultDurationMonths, defaultIsAcute]
  );
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [suggestedAcuteMedications, setSuggestedAcuteMedications] = useState(
    []
  );
  const [lastRegularDuration, setLastRegularDuration] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadMedications = async () => {
      setMedicationsLoading(true);

      const { data, ok, message } = await fetchMedications({
        page: 1,
        limit: 10000,
      });

      if (cancelled) return;

      if (!ok) {
        setMedicationOptions([]);
        setError(message || 'Doslo je do greske pri dobavljanju lekova.');
        setMedicationsLoading(false);
        return;
      }

      const options = Array.isArray(data?.medications)
        ? data.medications.map((medication) => ({
            value: medication.name,
            label: medication.name,
          }))
        : [];

      setMedicationOptions(options);
      setMedicationsLoading(false);
    };

    const loadAcuteTherapySuggestion = async () => {
      if (!initialModalForm.isAcute || !lockIsAcute || !user?._id) {
        return;
      }

      const { data, ok } = await fetchAcuteTherapySuggestion(user._id);

      if (cancelled || !ok) return;

      const suggestion = data?.data && typeof data.data === 'object'
        ? data.data
        : data;
      const suggestedMedications = Array.isArray(suggestion?.medications)
        ? suggestion.medications.filter(Boolean)
        : [];

      if (!suggestedMedications.length) return;

      setSuggestedAcuteMedications(suggestedMedications);
    };

    setForm(initialModalForm);
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setLoading(false);
    setConfirmVisible(false);
    setPendingPayload(null);
    setMedicationOptions([]);
    setSuggestedAcuteMedications([]);
    setLastRegularDuration(
      initialModalForm.isAcute ? '' : initialModalForm.durationMonths
    );

    loadMedications();
    loadAcuteTherapySuggestion();

    return () => {
      cancelled = true;
    };
  }, [initialModalForm, isOpen, lockIsAcute, user?._id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'durationMonths' && !form.isAcute) {
      setLastRegularDuration(value);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAcuteChange = (e) => {
    const checked = e.target.checked;

    setForm((prev) => ({
      ...prev,
      isAcute: checked,
      durationMonths: checked ? '1' : lastRegularDuration,
    }));
  };

  const handleSelectMedication = (value) => {
    setForm((prev) => {
      if (prev.medications.includes(value)) {
        return prev;
      }

      return {
        ...prev,
        medications: [...prev.medications, value],
      };
    });

    setMedicationSearch('');
  };

  const handleRemoveMedication = (index) => {
    setForm((prev) => ({
      ...prev,
      medications: prev.medications.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const submitTherapy = useCallback(
    async (payload) => {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      const { ok, message } = await createTherapy(payload);
      if (!ok) {
        setError(message);
        setLoading(false);
        return;
      }

      setSuccessMessage(message || 'Terapija kreirana.');
      setDone(true);
      onCreate?.();

      setTimeout(() => {
        setDone(false);
        onClose();
      }, 1000);

      setLoading(false);
    },
    [onClose, onCreate]
  );

  const handleOk = useCallback(async () => {
    if (!user?._id) {
      setError('Korisnik nije izabran.');
      return;
    }

    const centralDate = form.centralDate?.trim();
    if (!centralDate) {
      setError('Popunite centralni datum.');
      return;
    }
    if (!form.isAcute && !form.durationMonths) {
      setError('Izaberite trajanje terapije.');
      return;
    }

    const payload = {
      userId: user._id,
      centralDate,
      durationMonths: Number(form.isAcute ? '1' : form.durationMonths),
    };

    if (form.medications.length) payload.medications = form.medications;
    if (form.isAcute) payload.isAcute = true;
    if (form.isAcute && form.description?.trim()) {
      payload.description = form.description.trim();
    }

    if (form.isAcute) {
      await submitTherapy(payload);
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const { data, ok, message } = await fetchUserTherapies({
      userId: user._id,
      page: 1,
      limit: 100,
      status: 'current',
    });

    if (!ok) {
      setError(message || 'Doslo je do greske pri proveri aktuelne terapije.');
      setLoading(false);
      return;
    }

    const hasCurrentTherapy =
      Array.isArray(data?.therapies) &&
      data.therapies.some((therapy) => therapy?.isAcute !== true);

    setLoading(false);

    if (hasCurrentTherapy) {
      setPendingPayload(payload);
      setConfirmVisible(true);
      return;
    }

    await submitTherapy(payload);
  }, [
    form.centralDate,
    form.durationMonths,
    form.isAcute,
    form.medications,
    form.description,
    submitTherapy,
    user?._id,
  ]);

  const handleCancel = () => {
    setForm(initialModalForm);
    setMedicationSearch('');
    setError('');
    setSuccessMessage('');
    setDone(false);
    setConfirmVisible(false);
    setPendingPayload(null);
    onClose();
  };

  const handleConfirmAdd = async () => {
    if (!pendingPayload) return;
    setConfirmVisible(false);
    await submitTherapy(pendingPayload);
    setPendingPayload(null);
  };

  const handleConfirmCancel = () => {
    setConfirmVisible(false);
    setPendingPayload(null);
  };

  const userLabel = user
    ? user.userName?.trim() ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.email ||
      user.phone
    : '';

  const availableMedicationOptions = medicationOptions.filter(
    (option) => !form.medications.includes(option.value)
  );
  const isLockedAcuteTherapy = lockIsAcute && initialModalForm.isAcute;
  const availableSuggestedAcuteMedications = suggestedAcuteMedications.filter(
    (medication) => !form.medications.includes(medication)
  );

  return (
    <Modal
      className="create_user_modal"
      title={isLockedAcuteTherapy ? 'Dodaj akutnu terapiju' : 'Dodaj terapiju'}
      open={isOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Button
          key="create"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '150px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Dodaj"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Check Icon"
              className="check-icon"
            />
          ) : (
            'Dodaj'
          )}
        </Button>,
      ]}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <div style={{ marginBottom: 12 }}>
          <strong>Korisnik:</strong> {userLabel || '-'}
        </div>
        {!isLockedAcuteTherapy && (
          <div style={{ marginBottom: 12 }}>
            <Checkbox
              checked={form.isAcute}
              onChange={handleAcuteChange}
              disabled={lockIsAcute}
            >
              Akutna terapija
            </Checkbox>
            {form.isAcute && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                Akutna terapija traje 1 mesec.
              </div>
            )}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="centralDate"
            style={{ display: 'block', marginBottom: 6 }}
          >
            {isLockedAcuteTherapy ? 'Datum realizovanja' : 'Centralni datum'}
          </label>
          <DatePicker
            {...fixedBottomDatePickerProps}
            id="centralDate"
            placeholder="dd/mm/yyyy"
            format="DD/MM/YYYY"
            style={{ width: '100%', height: 52 }}
            value={form.centralDate ? dayjs(form.centralDate) : null}
            onChange={(date) =>
              setForm((prev) => ({
                ...prev,
                centralDate: date ? date.format('YYYY-MM-DD') : '',
              }))
            }
          />
        </div>
        {form.isAcute && (
          <div style={{ marginTop: 12 }}>
            <label
              htmlFor="description"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Napomena
            </label>
            <Input
              id="acuteTherapyNote"
              name="acuteTherapyNote"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              autoComplete="new-password"
              placeholder="Unesite napomenu"
              style={{ width: '100%', height: 52 }}
            />
          </div>
        )}
        {!isLockedAcuteTherapy && (
          <div style={{ marginTop: 12 }}>
            <label
              htmlFor="durationMonths"
              style={{ display: 'block', marginBottom: 6 }}
            >
              Trajanje terapije (meseci)
            </label>
            <select
              id="durationMonths"
              name="durationMonths"
              value={form.durationMonths}
              onChange={handleChange}
              disabled={form.isAcute || lockDurationMonths}
              style={{
                width: '100%',
                height: 38,
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                padding: '0 10px',
              }}
            >
              <option value="" disabled>
                Izaberite trajanje
              </option>
              {durationOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <label
            htmlFor="therapy-medication-select"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Lekovi
          </label>
          <Select
            id="therapy-medication-select"
            showSearch
            value={null}
            searchValue={medicationSearch}
            placeholder={
              medicationsLoading
                ? 'Ucitavanje lekova...'
                : 'Izaberite lek iz sistema'
            }
            style={{ width: '100%' }}
            loading={medicationsLoading}
            disabled={
              medicationsLoading || availableMedicationOptions.length === 0
            }
            options={availableMedicationOptions}
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            notFoundContent={
              medicationsLoading
                ? 'Ucitavanje lekova...'
                : 'Nema dostupnih lekova'
            }
            onSearch={setMedicationSearch}
            onSelect={handleSelectMedication}
          />
          <div className="therapy-med-list">
            {form.medications.map((medication, index) => (
              <div key={`${medication}-${index}`} className="therapy-med-chip">
                <span>{medication}</span>
                <button
                  type="button"
                  className="therapy-med-remove"
                  onClick={() => handleRemoveMedication(index)}
                >
                  x
                </button>
              </div>
            ))}
          </div>
          {isLockedAcuteTherapy &&
            availableSuggestedAcuteMedications.length > 0 && (
              <div className="therapy-med-suggestions">
                <div className="therapy-med-suggestions__label">
                  Predloženi lekovi
                </div>
                <div className="therapy-med-list therapy-med-suggestions__list">
                  {availableSuggestedAcuteMedications.map((medication) => (
                    <button
                      key={medication}
                      type="button"
                      className="therapy-med-chip therapy-med-suggestion"
                      onClick={() => handleSelectMedication(medication)}
                    >
                      {medication}
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
      <Modal
        className="therapy_confirm_modal"
        title="Pacijent već ima aktuelnu terapiju"
        open={confirmVisible}
        onOk={handleConfirmAdd}
        onCancel={handleConfirmCancel}
        okText="Dodaj"
        cancelText="Odustani"
        centered
        destroyOnHidden
      >
        Da li ste sigurni da želite da dodate novu?
      </Modal>
    </Modal>
  );
};

AddTherapyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  onCreate: PropTypes.func,
  defaultIsAcute: PropTypes.bool,
  lockIsAcute: PropTypes.bool,
  defaultDurationMonths: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  lockDurationMonths: PropTypes.bool,
};

export default AddTherapyModal;
